import React, { useState } from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';

// MUI components
import { alpha } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import NoContent from '@components/NoContentComponent/NoContent.jsx';
import useFetching from '@hooks/Fetching/Fetching.js';
import Attachments from '@pages/EventDetails/Tabs/AttachmentsTab/Attachments';
import Notes from '@pages/EventDetails/Tabs/NotesTab/Notes';

import { tabOptions } from './Constants';
import Agenda from './Tabs/AgendaTab/Agenda';
import { createSessionService } from './Tabs/AgendaTab/AgendaHelper.js';
import Billing from './Tabs/Billing/Billing';
import Consents from './Tabs/Consents/Consents';
import Information from './Tabs/InformationTab/Information';
import BookingParticipants from './Tabs/Participants/BookingParticipants';
import { tabConfig } from './Util';

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: '3.125rem',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    maxWidth: '100%'
  }
}));

const TAB_MIN_HEIGHT = '38vh';

const StyleBox = styled(Box)(({ theme }) => ({
  backgroundColor: get(theme.palette.primary, 'background'),
  backgroundImage: `linear-gradient(${alpha(get(theme.palette.common, 'white'), 0.05)}, ${alpha(get(theme.palette.common, 'white'), 0.05)})`,
  width: '100%',
  padding: '5px',
  marginTop: '1rem',
  marginBottom: '1rem'
}));

const TabPanel = ({ children, value, index, ...rest }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...rest}
  >
    {value === index && <Box sx={{ p: { xs: 0, sm: 3 } }}>{children}</Box>}
  </div>
);

const EventDetailsTabs = ({
  activeItem,
  handleItemClick,
  configuration,
  event,
  fetchBilling,
  fetchParticipants,
  bookingData,
  bookingParticipantsData,
  fetchConsents,
  consents,
  bookingNumberUuid
}) => {
  const [hasFetchedParticipants, setHasFetchedParticipants] = useState(false);

  const [sessionsData, areSessionsLoading] = useFetching(
    createSessionService.bind(null, event?.uuid)
  );
  const hasSessions = !!sessionsData?.data?.content?.length;

  const showItem = {
    overview: configuration?.EventsDetailsContent?.showOverviewTab,
    information: configuration?.EventsDetailsContent?.showInformationTab,
    participants: configuration?.EventsDetailsContent?.showParticipantsTab && !!bookingNumberUuid,
    billing:
      configuration?.EventsDetailsContent?.showBillingTab &&
      !!bookingNumberUuid &&
      event?.hasPricesAndDiscountsFeature,
    agenda: configuration?.EventsDetailsContent?.showAgendaTab && hasSessions,
    attachments: configuration?.EventsDetailsContent?.showAttachmentsTab,
    notes: configuration?.EventsDetailsContent?.showNotes,
    consents: configuration?.EventsDetailsContent?.showConsents && !!bookingNumberUuid
  };

  const a11yProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`
    };
  };

  const visibleTabs = Object.entries(showItem)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const createTabComponent = (tab) => {
    switch (tab) {
      case tabOptions.information.toLowerCase():
        return (
          <Information
            event={event}
            configuration={configuration}
            isBooking={!!bookingNumberUuid}
            minHeight={TAB_MIN_HEIGHT}
          />
        );
      case tabOptions.participants.toLowerCase():
        return (
          <BookingParticipants
            fetchBookingParticipants={fetchParticipants}
            bookingParticipantsData={bookingParticipantsData}
            fetchBilling={fetchBilling}
            bookingData={bookingData}
            activeItem={activeItem}
            hasFetched={hasFetchedParticipants}
            setHasFetched={setHasFetchedParticipants}
          />
        );

      case tabOptions.billing.toLowerCase():
        return (
          <Billing
            event={event}
            fetchBilling={fetchBilling}
            bookingData={bookingData}
            minHeight={TAB_MIN_HEIGHT}
          />
        );

      case tabOptions.agenda.toLowerCase():
        return (
          <Agenda
            sessionsData={sessionsData}
            isLoading={areSessionsLoading}
            venues={event?.venues}
            minHeight={TAB_MIN_HEIGHT}
          />
        );

      case tabOptions.attachments.toLowerCase():
        return (
          <Attachments
            eventParticipantMessage={event?.eventParticipantMessage}
            guid={event?.uuid}
            configColumns={configuration?.EventDetailsAttachmentsTab}
            minHeight={TAB_MIN_HEIGHT}
          />
        );

      case tabOptions.notes.toLowerCase():
        return (
          <Notes
            eventId={event?.uuid}
            advancedFilters={[
              {
                key: 'type',
                operation: 'equal',
                value: Localize.get('CustomerLoyaltyEvents.publicForFilter')
              }
            ]}
            minHeight={TAB_MIN_HEIGHT}
          />
        );

      case tabOptions.consents.toLowerCase():
        return (
          <Consents
            evendDataId={bookingData?.id}
            fetchConsents={fetchConsents}
            consents={consents}
            minHeight={TAB_MIN_HEIGHT}
          />
        );

      default:
        return (
          <Box data-testid={'noComponentMessage'}>
            <NoContent infoType={Localize.get('Booking.noComponent')} />
          </Box>
        );
    }
  };

  return (
    <StyleBox>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeItem}
          allowScrollButtonsMobile
          onChange={(event, newValue) => handleItemClick(event, newValue)}
          variant='scrollable'
          aria-label='Details Menu'
        >
          {visibleTabs.map((tab, index) => (
            <StyledTab
              key={`${tab}`}
              icon={tabConfig[tab]?.icon}
              iconPosition='start'
              label={Localize.get(`EventDetails.${tabConfig[tab]?.label}`)}
              wrapped
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>
      {visibleTabs.map((tab, index) => (
        <TabPanel key={tab} value={activeItem} index={index}>
          {createTabComponent(tab)}
        </TabPanel>
      ))}
    </StyleBox>
  );
};

export default EventDetailsTabs;
